import React, { useEffect, useState } from 'react';
import { QrReader } from 'react-qr-reader';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getTableByQrCode } from '../../services/table';
import { X } from 'lucide-react';

const SUPU_ACCENT = '#0D3031';

const LoadingScreen = ({ scannedTable, accentColor }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/80 p-6 backdrop-blur-sm">
    <div className="w-full max-w-xs rounded-2xl bg-white p-6 text-center">
      <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-transparent" style={{ borderTopColor: accentColor }} />
      <div className="text-lg font-semibold text-neutral-950">
        Opening table {scannedTable}
      </div>
      <p className="mt-1 text-sm text-neutral-500">Loading the restaurant menu.</p>
    </div>
  </div>
);

const QRScanner = ({ onClose }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [scannedTable, setScannedTable] = useState(null);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.overscrollBehavior = originalOverscroll;
    };
  }, []);

  const parseQRData = (text) => {
    // 1. Check for the new path-based format: /menu/:merchantId/:tableNumber/:revenueCenter
    try {
      const url = new URL(text.startsWith('http') ? text : `https://${text}`);
      const menuMatch = url.pathname.match(/\/menu\/([^/]+)\/([^/]+)(?:\/([^/]+))?/);
      if (menuMatch) {
        return {
          merchantId: menuMatch[1],
          table: decodeURIComponent(menuMatch[2]),
          revenueCenter: menuMatch[3] ? decodeURIComponent(menuMatch[3]) : 'default',
        };
      }
    } catch {}

    // 2. Check for JSON format
    try {
      const parsed = JSON.parse(text);
      if (parsed?.merchantId || parsed?.merchant_id) {
        return {
          merchantId: parsed.merchantId || parsed.merchant_id,
          table: parsed.tableNumber || parsed.table_number || parsed.table,
          revenueCenter: parsed.revenueCenter || parsed.revenue_center || 'default',
        };
      }
    } catch {}
    try {
      const params = new URLSearchParams(text);
      const merchantId = params.get('merchantId');
      const table = params.get('tableNumber');
      if (merchantId || table) {
        return {
          merchantId,
          table,
          revenueCenter: params.get('revenueCenter') || 'default',
        };
      }
    } catch {}
    try {
      const url = new URL(text.startsWith('http') ? text : `https://${text}`);
      const merchantId = url.searchParams.get('merchantId');
      const table = url.searchParams.get('tableNumber');
      if (merchantId && table) {
        return {
          merchantId,
          table,
          revenueCenter: url.searchParams.get('revenueCenter') || 'default',
        };
      }
    } catch {}
    return null;
  };

  const handleScan = async (result) => {
    if (!result) return;
    const raw = result?.getText ? result.getText() : result?.text || result?.toString?.() || '';
    if (!raw) return;
    try {
      const data = parseQRData(raw);
      if (data?.merchantId && data?.table) {
        setIsLoading(true);
        setScannedTable(data.table);
        await new Promise(resolve => setTimeout(resolve, 800));
        navigate(`/menu/${data.merchantId}/${data.table}/${data.revenueCenter}`);
        return;
      }
      const qrCodeId = extractQrCodeId(raw);
      if (qrCodeId) {
        setIsLoading(true);
        const res = await getTableByQrCode(qrCodeId);
        const table = res?.data?.table;
        // In some Supabase relationship queries, it might return an array if the foreign key isn't perfectly mapped as single
        const restaurantRaw = table?.restaurants;
        const restaurant = Array.isArray(restaurantRaw) ? restaurantRaw[0] : restaurantRaw;
        if (table && restaurant && restaurant.id) {
          setScannedTable(table.table_number);
          navigate(`/menu/${restaurant.id}/${table.table_number}/default`);
        } else {
          throw new Error('Table not found');
        }
        return;
      }
      toast.dismiss();
      toast.error('Invalid QR code format', { id: 'invalid-qr' });
    } catch {
      toast.dismiss();
      toast.error('Invalid QR code', { id: 'invalid-qr' });
    }
  };

  const extractQrCodeId = (text) => {
    try {
      const url = new URL(text.startsWith('http') ? text : `https://${text}`);
      const match = url.pathname.match(/\/order\/([^/]+)/);
      if (match) return match[1];
    } catch {}
    return null;
  };

  return (
    <div className="fixed inset-0 z-[9999] h-[100dvh] w-screen overflow-hidden bg-neutral-950">
      {isLoading ? (
        <LoadingScreen scannedTable={scannedTable} accentColor={SUPU_ACCENT} />
      ) : (
        <div className="relative h-[100dvh] w-screen overflow-hidden bg-neutral-950">
          <QrReader
            onResult={handleScan}
            constraints={{ facingMode: 'environment' }}
            containerStyle={{ width: '100%', height: '100%' }}
            videoContainerStyle={{ width: '100%', height: '100%', paddingTop: 0 }}
            videoStyle={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center' }}
            className="supu-qr-reader absolute inset-0 h-full w-full"
            scanDelay={500}
          />

          <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between bg-neutral-950/85 px-5 pb-4 pt-[calc(env(safe-area-inset-top)+1rem)] backdrop-blur-sm">
            <div>
              <h2 className="text-lg font-semibold text-white">Scan table code</h2>
              <p className="mt-1 text-sm text-white/70">Hold the QR code inside the frame.</p>
            </div>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-950 shadow-sm transition active:scale-95"
              aria-label="Close scanner"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0,transparent_145px,rgba(0,0,0,0.72)_146px)]" />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative h-[min(72vw,18rem)] w-[min(72vw,18rem)] rounded-3xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.08)]">
              <div className="absolute left-0 top-0 h-12 w-12 rounded-tl-3xl border-l-4 border-t-4" style={{ borderColor: SUPU_ACCENT }} />
              <div className="absolute right-0 top-0 h-12 w-12 rounded-tr-3xl border-r-4 border-t-4" style={{ borderColor: SUPU_ACCENT }} />
              <div className="absolute bottom-0 left-0 h-12 w-12 rounded-bl-3xl border-b-4 border-l-4" style={{ borderColor: SUPU_ACCENT }} />
              <div className="absolute bottom-0 right-0 h-12 w-12 rounded-br-3xl border-b-4 border-r-4" style={{ borderColor: SUPU_ACCENT }} />
              <div className="absolute inset-x-8 top-1/2 h-px animate-scan rounded-full" style={{ backgroundColor: SUPU_ACCENT }} />
            </div>
          </div>
          <div className="absolute bottom-[calc(env(safe-area-inset-bottom)+1.5rem)] left-5 right-5 z-20 text-center">
            <p className="mx-auto max-w-xs rounded-full bg-white px-4 py-3 text-sm font-semibold text-neutral-950 shadow-lg">
              Keep the code inside the square
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner;
